import PrintIcon from "@mui/icons-material/Print";
import React, { useEffect, useRef } from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Divider,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography
} from "@mui/material";
import logoExtended from "../../../../assets/images/paisley-logo-extended.png";
import { useDispatch, useSelector } from "react-redux";
import useQuery from "../../../../hooks/useQuery";
import { successPayment } from "../../../../redux/actions/signatory";
import State, { StateStatus } from "../../../../../domain/entities/State";
import { formatDate } from "../../../../utils/date-formatter";
import { useReactToPrint } from "react-to-print";

const formatValue = (value?: string | number) => value ?? "---";

const PaymentSuccess: React.FC = () => {
    const query = useQuery();
    const dispatch: any = useDispatch();
    const paymentId = query.get("payId");
    const payment = useSelector((state: State) => state.signatory.payment);
    const paymentLoadStatus = useSelector(
        (state: State) => state.signatory.paymentLoadStatus
    );

    const printRef = useRef<HTMLDivElement>(null);
    const handlePrint = useReactToPrint({
        contentRef: printRef,
        documentTitle: 'Payment Receipt',
        preserveAfterPrint: false,
    });

    useEffect(() => {
        if (paymentId) {
            dispatch(successPayment(paymentId));
        }
    }, []);

    const isCompleted = payment?.status === "COMPLETED";
    const isPending = payment?.status === "PENDING";
    const currencySymbol = "$";
    const statusText = isCompleted ? "Paid" : isPending ? "Pending" : formatValue(payment?.status);
    const amountText = isCompleted
        ? `${currencySymbol}${(Number(payment?.amount || 0) / 100).toFixed(2)}`
        : "---";
    const creationTime = payment?.creationTime ? formatDate(payment.creationTime) : "---";
    const paymentTime = payment?.paymentTime ? formatDate(payment.paymentTime) : "---";

    return (
        <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            height="100vh"
            bgcolor="#f5f5f5"
        >
            {paymentLoadStatus === StateStatus.LOADING ? (
                <CircularProgress />
            ) : ((paymentLoadStatus === StateStatus.COMPLETE && !payment) || !paymentId) ? (
                <Card sx={{ minWidth: 300, textAlign: "center", p: 4 }}>
                    <CardContent>
                        <Box display="flex" justifyContent="center" mb={3}>
                            <img src={logoExtended} alt="Paisley Logo" style={{ maxWidth: "200px", height: "auto" }} />
                        </Box>
                        <Typography variant="h5" gutterBottom>
                            Payment not found
                        </Typography>
                    </CardContent>
                </Card>
            ) : (
                <Box
                    ref={printRef}
                    bgcolor="white"
                    p={4}
                    borderRadius={2}
                    boxShadow={3}
                    width="90%"
                    maxWidth="900px"
                    position="relative"
                    sx={{
                        '@media print': {
                            width: '210mm',
                            margin: '0 auto',
                            boxShadow: 'none',
                            borderRadius: 0,
                            backgroundColor: 'transparent',
                        },
                    }}
                >
                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={handlePrint}
                        className="no-print"
                        sx={{ 
                            position: "absolute",
                            top: 16,
                            right: 16,
                            '@media print': {
                                display: 'none',
                            }
                        }}
                    >
                        <PrintIcon />
                    </Button>

                    <Box display="flex" justifyContent="center" mb={3}>
                        <img src={logoExtended} alt="Paisley Logo" style={{ maxWidth: "200px", height: "auto" }} />
                    </Box>

                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={4}>
                            <Typography variant="h4" fontWeight={600}>
                                {payment?.name}
                            </Typography>
                            <Typography variant="body2">{formatValue(payment?.signatoryName)}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={2}>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>Method</Typography>
                            <Typography variant="body2">{formatValue(payment?.payMethod)}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={2}>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>Status</Typography>
                            <Typography variant="body2">{statusText}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={2}>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>Amount</Typography>
                            <Typography variant="body2">{amountText}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={2}>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>Date</Typography>
                            <Typography variant="body2">{creationTime}</Typography>
                        </Grid>
                    </Grid>

                    <Divider sx={{ my: 3 }} />

                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="h6">Bill To</Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                Address: <span style={{ color: 'black' }}>{formatValue(payment?.payAddress)}</span>
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                Email: <span style={{ color: 'black' }}>{formatValue(payment?.payEmail)}</span>
                            </Typography>
                        </Grid>
                    </Grid>

                    <Box mt={4}>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ backgroundColor: 'grey.100' }}>
                                    <TableCell align="center"><strong>Created Date</strong></TableCell>
                                    <TableCell align="center"><strong>Payment Date</strong></TableCell>
                                    <TableCell align="right"><strong>Amount</strong></TableCell>
                                    <TableCell align="right"><strong>Payment Value</strong></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <TableRow>
                                    <TableCell align="center">{creationTime}</TableCell>
                                    <TableCell align="center">{paymentTime}</TableCell>
                                    <TableCell align="right">{amountText}</TableCell>
                                    <TableCell align="right">
                                        {payment?.status === 'COMPLETED' ? amountText : '---'}
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell colSpan={3} align="right">
                                        <Typography variant="body2" fontWeight={600}>
                                            Total Payment Amount:
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="body2">{amountText}</Typography>
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default PaymentSuccess;
